import React, { useEffect, useLayoutEffect, useState } from 'react';

// @ts-ignore
import { tracking } from '@iwoca/frontend-tracking-library';
import { Card, Icon, Pill } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { getValues } from 'redux-form';

import { useGetSplitTestQuery } from '@lendingAPI/lendingAPIQueries';

import LimitedCompanyInformation from './LimitedCompanyInformation';
import styles from './PersonalInformation.module.css';
import { Application, UI } from './types/PersonalInformation.types';
import { toggleCountryModal as toggleCountryModalAction } from '../../actions';
import FormStepHeading from '../../components/FormStepHeading/FormStepHeading';
import { buildApplicationState } from '../../helpers/applicationManager';
import { getCountryCode } from '../../lendingAPI/lendingAPISelector';
import { RootState } from '../../reducers';
import { getAmplitudeDefaultEventProperties } from '../../tracking/amplitudeEvents';
import commonMessages from '../Containers.messages';
import { getLoanDecision as getLoanDecisionAction } from '../LoanDecision/LoanDecision.actions';
import CountryErrorModal from '../Modals/CountryErrorModal';

const mapStateToProps = (state: RootState) => {
    const { form, ui, fetched } = state;

    return {
        formValues: getValues(form.sign_up),
        ui: ui,
        application: fetched.application,
        countryCode: getCountryCode(state)
    };
};

const mapDispatchToProps = {
    toggleCountryModal: () => toggleCountryModalAction(),
    getLoanDecision: () => getLoanDecisionAction()
};

type PersonalInformationProps = {
    ui: UI;
    formValues: any;
    application: Application;
    toggleCountryModal: () => void;
    getLoanDecision: () => void;
    countryCode: 'de' | 'uk';
};

const PersonalInformation = ({
    ui,
    formValues,
    application,
    toggleCountryModal,
    getLoanDecision,
    countryCode
}: PersonalInformationProps) => {
    const [isShareholderError, setIsShareholderError] = React.useState(false);
    const [closeNoPG, setCloseNoPG] = useState(false);
    const { branch } = useGetSplitTestQuery('signup_no_pg_test');

    useEffect(() => {
        tracking.ampli.stepLoaded({
            'Step name': 'Personal details',
            ...getAmplitudeDefaultEventProperties()
        });
    }, []);

    useLayoutEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const getCompanyType = () => {
        const state = buildApplicationState(application.payload, formValues);
        return state.company.type;
    };

    const onCountrySelect = (selectedOption: string) => {
        if (countryCode !== selectedOption) {
            toggleCountryModal();
        }
    };

    const goToNextStep = () => {
        tracking.ampli.personalDetailsSubmitted({
            ...getAmplitudeDefaultEventProperties()
        });

        getLoanDecision();
    };

    const companyType = getCompanyType();

    return (
        <>
            <div>
                <FormStepHeading>
                    <FormattedMessage {...commonMessages.personalInformation} />
                </FormStepHeading>
                {!closeNoPG && branch === 'include' && (
                    <Card className={styles.noPGContainer}>
                        <Pill variant="green">NEW</Pill>
                        <p
                            data-testid="no-pg-description"
                            className={styles.noPGDescription}>
                            We’re now offering loans with{' '}
                            <strong>no personal guarantee</strong>. Complete
                            your application to find out if you qualify.
                        </p>
                        <Icon
                            icon="timesDefault"
                            className={styles.noPGClose}
                            onClick={() => setCloseNoPG(true)}
                        />
                    </Card>
                )}
                {(() => {
                    switch (companyType) {
                        case 'st':
                        case 'op':
                        case 'other':
                        case 'llp':
                        case 'ltd':
                        case 'plc':
                            const validateIsShareholder = (applicant: {
                                uid: string | number;
                            }) => {
                                if (ui.answeredIsShareholder[applicant.uid]) {
                                    return true;
                                }

                                setIsShareholderError(true);
                                return false;
                            };

                            return (
                                <LimitedCompanyInformation
                                    ui={ui}
                                    nextStep={goToNextStep}
                                    formKey="personalInformation"
                                    onCountrySelect={onCountrySelect}
                                    isShareholderError={isShareholderError}
                                    validateIsShareholder={
                                        validateIsShareholder
                                    }
                                />
                            );
                        case 'partnership':
                        default:
                            return null;
                    }
                })()}
                <CountryErrorModal />
            </div>
        </>
    );
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PersonalInformation);
